/**
 * Where this app is mounted, as the BROWSER sees it.
 *
 * Vite bakes `BASE_URL` at build time, and it was used directly as the router's
 * basename and as the API prefix. That works for exactly one mount point. The
 * service is reachable at two:
 *
 *   https://artifacts.teleste.systems/hil/   - a sub-path on the shared host
 *   https://hil.teleste.systems/             - its own subdomain
 *
 * and the bundle is built with base `/hil/`. On the subdomain the assets still
 * load (its nginx accepts both prefixes), the JavaScript still runs - and the
 * router matches `/` against a basename of `/hil/`, finds nothing, and renders
 * nothing. A completely blank page with a 200, no console error and every asset
 * fetched: the failure looks like the service is down when it is serving
 * perfectly.
 *
 * So the prefix is decided from the URL actually being visited rather than from
 * the build. One bundle, both mount points, and a third one later needs no
 * rebuild. The server is unaffected either way: nginx strips the prefix, so it
 * always sees `/`.
 *
 * The session cookie is set with `path=/`, so it is sent under either prefix.
 */
const BUILT = (import.meta.env.BASE_URL || "/").replace(/\/$/, "");

/**
 * The prefix to use, given a pathname. Exported for tests, which is the only
 * way to check this without deploying it somewhere.
 */
export function mountFor(pathname: string, built: string = BUILT): string {
  if (!built) return "";
  // Exactly the base, or the base followed by a separator. A bare
  // `startsWith(built)` would treat `/hilarious` as mounted under `/hil`.
  return pathname === built || pathname.startsWith(built + "/") ? built : "";
}

export const MOUNT = mountFor(
  typeof window === "undefined" ? BUILT : window.location.pathname,
);
